import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroupConfig {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterBarProps {
  groups: FilterGroupConfig[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  sorts: FilterOption[];
  sortValue: string;
  onSortChange: (value: string) => void;
}

export default function FilterBar({
  groups,
  values,
  onChange,
  sorts,
  sortValue,
  onSortChange,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-3 md:p-4 border-b border-background-200">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <span className="w-6 h-6 flex items-center justify-center text-primary-600">
            <i className="ri-filter-3-line text-lg"></i>
          </span>
          内容筛选
        </div>

        <div className="hidden md:flex items-center gap-1 p-1 rounded-full bg-background-100">
          {sorts.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => onSortChange(s.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                sortValue === s.value
                  ? "bg-background-50 text-primary-600 border border-primary-200"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line"></i>
          {open ? "收起" : "筛选"}
        </button>
      </div>

      <div className={`${open ? "block" : "hidden"} md:block p-3 md:p-4`}>
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <span className="shrink-0 w-16 text-sm text-foreground-500 font-label">
                {group.label}
              </span>
              <div className="flex flex-wrap gap-2">
                {group.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(group.key, opt.value)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      values[group.key] === opt.value
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex md:hidden items-center gap-1 p-1 rounded-full bg-background-100 mt-4 w-full">
          {sorts.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => onSortChange(s.value)}
              className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                sortValue === s.value
                  ? "bg-background-50 text-primary-600 border border-primary-200"
                  : "text-foreground-600"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}